import {
  decodeFilterByIdList,
  filterByIdListWithUnknownPayload,
} from "@/domain/view/components/datatable/datatable-filters/functions/filter-by-id-list";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import {
  DatatableColumn,
  DatatableRowData,
  isRawRow,
  isSectionRow,
  isSubSectionRow,
} from "@/domain/view/components/datatable/datatable-types";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { UseCreateAssigneeProps } from "../../hooks/api/use-create-assignee";
import { getAssigneeSectionKey, sortAssignee } from "../../models/assignee-model";
import { Assignee } from "../../models/assignee-model";

import { DatatableAssigneeAppliedFilterView } from "./datatable-assignee-applied-filter-view";
import { DatatableAssigneeConfigurationView } from "./datatable-assignee-configuration-view";
import { DatatableAssigneeDisplayView } from "./datatable-assignee-display-view";
import { DatatableAssigneeSectionHeader } from "./datatable-assignee-section-header";
import { DatatableAssigneeSubSectionHeader } from "./datatable-assignee-subsection-header";
import { DatatableMultiAssigneeCell } from "./datatable-multi-assignee-cell";

type OwnerType = UseCreateAssigneeProps["ownerType"];

export interface DatatableMultiAssigneeTemplateColumnProps<TData extends DatatableRowData> {
  workspace: Workspace;
  columnId?: string;
  orderValue?: number;
  displayValue?: string;
  editable?: boolean;
  assigneeListFromRow: (row: TData) => Assignee[];
  teamIdListForRow: (row: TData) => string[];
  ownerIdFromRow: (row: TData) => string;
  ownerTypeFromRow: (row: TData) => OwnerType;
}

export const DatatableMultiAssigneeTemplateColumn = <TData extends DatatableRowData>({
  workspace,
  columnId = "assignee",
  orderValue = 103,
  displayValue = "Verantwortlich",
  editable = false,
  assigneeListFromRow,
  teamIdListForRow,
  ownerIdFromRow,
  ownerTypeFromRow,
}: DatatableMultiAssigneeTemplateColumnProps<TData>): DatatableColumn<TData> => {
  // Helper to get first assignee for sorting/sectioning
  const getFirstAssignee = (row: TData): Assignee | null => {
    const list = assigneeListFromRow(row);
    return list.length > 0 ? list[0] : null;
  };

  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue,
    enableSectioning: true,
    getSectionKey: (data) => getAssigneeSectionKey(getFirstAssignee(data)),
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId,
        enableHiding: true,
        meta: { enableDragging: true },
        enableSorting: true,
        enableColumnFilter: true,
        size: 1,
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          const assigneeA = getFirstAssignee(rowA.original);
          const assigneeB = getFirstAssignee(rowB.original);

          return sortAssignee(assigneeA, assigneeB, false);
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            return true;
          }

          const assigneeList = assigneeListFromRow(row.original);
          // Match if any assignee matches the filter
          if (assigneeList.length === 0) {
            return filterByIdListWithUnknownPayload(value, undefined);
          }
          return assigneeList.some((assignee) => filterByIdListWithUnknownPayload(value, assignee.userId));
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className={`text-xs font-normal`}>
            {displayValue}
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          const assigneeList = assigneeListFromRow(row.original);
          const ownerId = ownerIdFromRow(row.original);
          const ownerType = ownerTypeFromRow(row.original);
          const rowTeamIdList = teamIdListForRow(row.original);
          return (
            <DatatableMultiAssigneeCell
              assigneeList={assigneeList}
              workspaceId={workspace.id}
              ownerId={ownerId}
              ownerType={ownerType}
              teamIdList={rowTeamIdList}
              editable={editable}
            />
          );
        },
      };
    },
    renderDisplayView: () => <DatatableAssigneeDisplayView displayValue={displayValue} />,
    getFilterConfigurationViewType: () => "popover",
    renderFilterConfigurationView: (onFinished: () => void) => {
      return (
        <DatatableAssigneeConfigurationView workspaceId={workspace.id} columnId={columnId} onFinished={onFinished} />
      );
    },
    renderAppliedFilterView: () => (
      <DatatableAssigneeAppliedFilterView workspaceId={workspace.id} columnId={columnId} displayValue={displayValue} />
    ),
    renderSectionHeader: (data, row) => {
      // Get user data from the first row in the section to avoid individual queries
      const firstRow = data.data[0];
      const user =
        firstRow && !isSectionRow(firstRow) && !isSubSectionRow(firstRow)
          ? getFirstAssignee(firstRow)?.user
          : null;

      return <DatatableAssigneeSectionHeader data={data} currentRow={row} user={user} />;
    },
    renderSubSectionHeader: (data, row) => {
      // Get user data from the first row in the subsection to avoid individual queries
      const firstRow = data.data[0];
      const user =
        firstRow && !isSectionRow(firstRow) && !isSubSectionRow(firstRow)
          ? getFirstAssignee(firstRow)?.user
          : null;

      return <DatatableAssigneeSubSectionHeader data={data} currentRow={row} user={user} />;
    },
    sortSection: (a, b) => {
      if (!a.key && !b.key) {
        return 0;
      }
      if (!a.key) {
        return 1;
      }
      if (!b.key) {
        return -1;
      }

      if (!isSectionRow(a) || !isSectionRow(b)) {
        return 0;
      }

      const firstEntryA = a.data[0];
      const firstEntryB = b.data[0];

      if (isSubSectionRow(firstEntryA) && isSubSectionRow(firstEntryB)) {
        const assigneeA = getFirstAssignee(firstEntryA.data[0]);
        const assigneeB = getFirstAssignee(firstEntryB.data[0]);
        return sortAssignee(assigneeA, assigneeB, false);
      }

      if (isRawRow(firstEntryA) && isRawRow(firstEntryB)) {
        const assigneeA = getFirstAssignee(firstEntryA);
        const assigneeB = getFirstAssignee(firstEntryB);
        return sortAssignee(assigneeA, assigneeB, false);
      }

      return 0;
    },
    sortSubSection: (a, b) => {
      if (!a.key && !b.key) {
        return 0;
      }
      if (!a.key) {
        return 1;
      }
      if (!b.key) {
        return -1;
      }

      if (!isSubSectionRow(a) || !isSubSectionRow(b)) {
        return 0;
      }

      const firstEntryA = a.data[0];
      const firstEntryB = b.data[0];

      if (isRawRow(firstEntryA) && isRawRow(firstEntryB)) {
        const assigneeA = getFirstAssignee(firstEntryA);
        const assigneeB = getFirstAssignee(firstEntryB);
        return sortAssignee(assigneeA, assigneeB, false);
      }

      return 0;
    },
    resolveStateFromUrlParams: (urlParams: URLSearchParams) => {
      const filter = decodeFilterByIdList(columnId, urlParams);
      if (filter) {
        return {
          columnFilters: [
            {
              id: columnId,
              value: filter,
            },
          ],
        };
      }
      return undefined;
    },
  };
};
